import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { DATA_MODEL_LIST_CDW_OBJECTS } from '../../../../utils/apollo/dataModeler/queries';
import {
  ICdwObjectsContainerFieldsFragment,
  ICdwTableFieldsFragment,
  IDataModelListCdwObjectsQuery,
  IDataModelListCdwObjectsQueryVariables,
} from '../../../../utils/apollo/graphqlSchema';
import { ContentLoader } from '../../../../components/Loaders';
import CdwTableContainer from './CdwTableContainer';
import {
  CdwObjectsFormHeader,
  CdwObjectsFormWrapper,
  HeaderSearchItem,
  HeaderTitleItem,
} from './SelectCdwTableFormUI';
import CdwTableErrors from './CdwTableErrors';
import CdwTableContainerPath from './CdwTableContainerPath';
import SearchInput from '../../../../components/Form/SearchInput';
import { useDataModelerContext } from '../../DataModelerContext';

interface IProps {
  selectedTable: ICdwTableFieldsFragment | null;
  onSelectTable: (table: ICdwTableFieldsFragment | null) => void;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
}

const SelectCdwTableForm = ({
  selectedTable,
  onSelectTable,
  searchQuery,
  setSearchQuery,
}: IProps) => {
  const { draftEdits, schemaObj } = useDataModelerContext();
  const [serverErrors, setServerErrors] = useState<string[]>([]);

  const [path, setPath] = useState<string[] | null>(
    draftEdits.dataSource || !schemaObj
      ? null
      : schemaObj?.dsConfig.path.map((pathComponent) => pathComponent.name),
  );

  const [currentContainer, setCurrentContainer] =
    useState<ICdwObjectsContainerFieldsFragment | null>(null);

  const onCompleted = (data: IDataModelListCdwObjectsQuery) => {
    const errors = data.dataModelListCdwObjects.errors;
    setServerErrors(errors);
    const container = data.dataModelListCdwObjects.result;
    if (container) {
      setCurrentContainer(container);
      if (path === null) {
        setPath(container.path.map((pathComponent) => pathComponent.name));
      }
    } else {
      setCurrentContainer(null);
    }
  };

  const { loading } = useQuery<
    IDataModelListCdwObjectsQuery,
    IDataModelListCdwObjectsQueryVariables
  >(DATA_MODEL_LIST_CDW_OBJECTS, {
    variables: {
      schemaKey:
        draftEdits.dataSource?.connection || !schemaObj?.key
          ? null
          : schemaObj?.key,
      connection: draftEdits.dataSource?.connection ?? null,
      path,
    },
    skip:
      currentContainer != null &&
      path != null &&
      currentContainer.path.join('.') === path.join('.'),
    onCompleted,
  });

  const openParent = (index?: number) => {
    const validPath = path ?? [];
    if (validPath.length > 0) {
      const endIndex = index ?? validPath.length - 1;
      setPath(validPath.slice(0, endIndex));
      onSelectTable(null);
    }
  };

  const openContainer = (name: string) => {
    const validPath = path ?? [];
    // TODO implement cache abd also set the container if cached
    setPath([...validPath, name]);
    onSelectTable(null);
  };

  if (loading) {
    return (
      <CdwObjectsFormWrapper>
        <ContentLoader />;
      </CdwObjectsFormWrapper>
    );
  }
  return (
    <>
      <CdwObjectsFormHeader>
        {currentContainer && (
          <CdwTableContainerPath
            path={currentContainer.path}
            openParent={openParent}
          />
        )}
        <HeaderTitleItem>Select Table</HeaderTitleItem>
        <HeaderSearchItem>
          <SearchInput
            placeholder={'Search'}
            value={searchQuery}
            onChange={setSearchQuery}
          />
        </HeaderSearchItem>
      </CdwObjectsFormHeader>
      <CdwObjectsFormWrapper>
        {currentContainer && (
          <CdwTableContainer
            searchQuery={searchQuery}
            container={currentContainer}
            openContainer={openContainer}
            currentTable={selectedTable}
            onSelectTable={onSelectTable}
          />
        )}
        <CdwTableErrors errors={serverErrors} />
      </CdwObjectsFormWrapper>
    </>
  );
};

export default SelectCdwTableForm;
